import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Trophy, TrendingDown, TrendingUp } from "lucide-react";

const num = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));

function Row({ row, rank, best }) {
  const improving = (row.delta_pct ?? 0) < 0;
  const Icon = improving ? TrendingDown : TrendingUp;
  const colour = improving ? "#156F5B" : "#B91C1C";
  const width = Math.max(6, (Math.abs(row.delta_pct) / best) * 100);
  return (
    <div data-testid={`leader-row-${rank}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs font-heading font-bold text-[#9CA3AF] w-4">{rank}</span>
          <Link to={`/sites/${encodeURIComponent(row.site_id)}/energy`} className="text-sm font-heading font-semibold text-[#212121] truncate hover:text-[#156F5B]">
            {row.site_dis}
          </Link>
          {row.saved_kwh != null && (
            <span className="k-sublabel shrink-0">{num(Math.abs(row.saved_kwh))} kWh</span>
          )}
        </div>
        <span className="text-sm font-heading font-semibold shrink-0 inline-flex items-center gap-1" style={{ color: colour }}>
          <Icon className="h-3.5 w-3.5" />{row.delta_pct > 0 ? "+" : ""}{row.delta_pct}%
        </span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${width}%`, background: colour, transition: "width .4s ease" }} />
      </div>
    </div>
  );
}

/** Celebrates the sites improving most on last year, and flags the ones drifting up. */
export function SavingsLeaderboard({ sites, prevYear }) {
  const { winners, laggards, best } = useMemo(() => {
    const scored = (sites || []).filter((s) => s.delta_pct != null);
    const sorted = [...scored].sort((a, b) => a.delta_pct - b.delta_pct);
    return {
      winners: sorted.filter((s) => s.delta_pct < 0).slice(0, 5),
      laggards: sorted.filter((s) => s.delta_pct > 0).reverse().slice(0, 3),
      best: Math.max(1, ...scored.map((s) => Math.abs(s.delta_pct))),
    };
  }, [sites]);

  if (winners.length === 0 && laggards.length === 0) return null;

  return (
    <div className="mt-6 k-card p-6" data-testid="savings-leaderboard">
      <div className="k-label flex items-center gap-2"><Trophy className="h-3.5 w-3.5" /> Savings leaderboard</div>
      <p className="mt-1 k-sublabel">Change in electricity against {prevYear}, matching months only.</p>

      {winners.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="leaderboard-winners">
          {winners.map((row, i) => <Row key={row.site_id} row={row} rank={i + 1} best={best} />)}
        </div>
      )}

      {laggards.length > 0 && (
        <div className="mt-6 pt-5 border-t border-[#F0F2F1]">
          <div className="k-sublabel mb-3">Using more than last year</div>
          <div className="space-y-3" data-testid="leaderboard-laggards">
            {laggards.map((row, i) => <Row key={row.site_id} row={row} rank={i + 1} best={best} />)}
          </div>
        </div>
      )}
    </div>
  );
}
